import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Skeleton } from '../../../../components/ui/Skeleton';
import DispatchStatusBadge from './DispatchStatusBadge';
import ReminderMessageModal from './ReminderMessageModal';
import { DispatchDetail, ViewMessageButton, hasViewableMessage, useDispatchDates } from './dispatchParts';
import { CHANNEL_LABELS, TRIGGER_LABELS } from '../utils/collectionReminders';

/**
 * Recordatorios de una nota (automáticos, programados y manuales) con su resultado, en el
 * detalle de la nota. Los datos los carga `useNoteReminders`.
 *
 * @param {{ history: object[], loading: boolean,
 *   error: { status: number|null, message: string|null } | null,
 *   onRetry: () => void, timeZone?: string }} props
 */
export default function ReminderHistoryCard({ history, loading, error, onRetry, timeZone }) {
  const { t } = useTranslation();
  const datesOf = useDispatchDates(timeZone);
  // Fila cuyo mensaje se está viendo ("Ver mensaje"), o null.
  const [viewing, setViewing] = useState(null);

  let content;
  if (loading && history.length === 0) {
    content = (
      <div className="space-y-2 p-4" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  } else if (error && history.length === 0) {
    content = (
      <div className="flex flex-wrap items-center justify-between gap-2 p-4 text-sm text-status-error" role="alert">
        <span>{error.message || t('No se pudo cargar el historial de recordatorios.')}</span>
        <Button size="sm" variant="outline" onClick={onRetry}>
          {t('Reintentar')}
        </Button>
      </div>
    );
  } else if (history.length === 0) {
    content = (
      <p className="bill-empty m-0" style={{ padding: '1.5rem' }}>
        {t('Todavía no se enviaron recordatorios de esta nota.')}
      </p>
    );
  } else {
    content = (
      <ul className="m-0 list-none divide-y divide-border p-0">
        {history.map((d) => {
          const dates = datesOf(d);
          return (
            <li key={d._id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-3 text-sm">
              <div className="min-w-0 space-y-0.5">
                <div className="font-medium text-fg">
                  {t(CHANNEL_LABELS[d.channel] ?? d.channel)} · {t(TRIGGER_LABELS[d.trigger] ?? d.trigger)}
                </div>
                <div className="bill-td-sub">
                  {dates.main}
                  {dates.sub && ` · ${dates.sub}`}
                </div>
                {d.recipientsMasked?.length > 0 && (
                  <div className="bill-td-sub break-all">{d.recipientsMasked.join(', ')}</div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 text-right">
                <DispatchStatusBadge status={d.status} size="sm" />
                {(d.errorCode || Number(d.attempts) > 1) && (
                  <span className="text-xs text-fg-muted">
                    <DispatchDetail dispatch={d} />
                  </span>
                )}
                {/* Solo si billing tiene el texto de esta fila (no alcanza con messageRef). */}
                {hasViewableMessage(d) && <ViewMessageButton onClick={() => setViewing(d)} />}
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="bill-card bill-card--clip">
      <div className="bill-card__head">
        <h3 className="bill-card__title">
          <BellRing className="mr-1.5 inline h-4 w-4 align-[-2px]" aria-hidden />
          {t('Recordatorios de esta nota')}
        </h3>
      </div>
      {content}
      {viewing && (
        <ReminderMessageModal dispatch={viewing} onClose={() => setViewing(null)} timeZone={timeZone} />
      )}
    </div>
  );
}
